import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Badge from '@mui/material/Badge';
import { Typography } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';

import { OButton, ODrawer, OLoader, OPopper, OSpinner } from '@src/shared';

import { toKFormat } from '@src/utils';
import { useRoomContext } from '@src/contexts';
import { useChatStore, useDrawerStore, usePeersStore, useRoomStore, useCalculatorStore } from '@src/stores';
import { useCalculatorActions } from '@src/hooks';

import { exportQuestionAndAnswerCSV } from '@src/api/endpoints/webinar';

import { sendMessageToAndroid } from '@src/bridge/sender';
import { generateUID } from '@src/bridge/handlers/share';

import { GoGear } from 'react-icons/go';
import { AiFillWechat } from 'react-icons/ai';
import { TbBackground } from 'react-icons/tb';
import { VscThreeBars } from 'react-icons/vsc';
import { CalculatorLogo, QuestionAndAnswerIcon } from '@src/assets/icons';
import { MdBarChart, MdEditNote, MdGroups } from 'react-icons/md';
import { CalculatorHeader } from '@src/features/calculator/components';
import { useCalculatorHandlers } from '@src/features/calculator/hooks';

import { EWebViewEvents } from '@src/bridge/types';
import { EMessageType, EUserType } from '@src/types';

import styles from './drawerController.module.scss';

const Notes = lazy(() => import('@src/components/Notes'));
const ChatBox = lazy(() => import('@src/components/ChatBox'));
const PollsBox = lazy(() => import('@src/components/PollsBox'));
const ChatDropdown = lazy(() => import('@src/components/ChatDropdown'));
const ParticipantsList = lazy(() => import('@src/components/ParticipantsList'));
const QuestionAndAnswer = lazy(() => import('@src/components/QuestionAndAnswer'));
const SettingsContent = lazy(() => import('@src/components/SettingsModal/SettingContent'));
const VirtualBackground = lazy(() => import('@src/components/BackgroundEffects/VirtualBackground'));
const Calculator = lazy(() => import('@src/features/calculator/Calculator'));

const VIRTUAL_BACKGROUND_TITLE = 'Virtual Backgrounds';

const DrawerController: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    currentPeer,
    isPollEnabled,
    isMobileDevice,
    isHostOrCohost,
    participantsPeers,
    isPublicChatEnabled,
    isPrivateChatEnabled,
    hasChatNotifications,
    hasActiveLimitedPolls,
  } = useRoomContext();

  const { selectedKey, isDrawerOpen, toggleDrawer } = useDrawerStore();

  const { meetingType, roomTitle, roomId, setIsRoomSettingsOpen, isRoomSettingsOpen } = useRoomStore(
    useShallow((state) => ({
      roomId: state.roomId,
      meetingType: state.meetingType,
      isJoinedRoom: state.isJoinedRoom,
      roomTitle: state.roomTitle,
      isRoomSettingsOpen: state.isRoomSettingsOpen,
      setIsRoomSettingsOpen: state.setIsRoomSettingsOpen,
    }))
  );

  const { viewersCount } = usePeersStore(
    useShallow((state) => ({
      viewersCount: state.viewersCount,
    }))
  );

  const { setSelectedReceiverData } = useChatStore(
    useShallow((state) => ({
      setSelectedReceiverData: state.setSelectedReceiverData,
    }))
  );

  const { setIsPersonalCalculatorVisible, sharedCalculatorData } = useCalculatorStore(
    useShallow((state) => ({
      setIsPersonalCalculatorVisible: state.setIsPersonalCalculatorVisible,
      sharedCalculatorData: state.sharedCalculatorData,
    }))
  );

  // Get calculator state for CalculatorHeader in drawer
  const calculatorHandlers = useCalculatorHandlers({ isReadOnly: false });
  const { mode: calculatorMode, handleModeChange: handleCalculatorModeChange } = calculatorHandlers;

  const { handleStopShareCalculator } = useCalculatorActions();

  const handleExportCSV = useCallback(async () => {
    try {
      setIsLoading(true);
      await exportQuestionAndAnswerCSV(roomId);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    } finally {
      setIsLoading(false);
    }
  }, [roomId]);

  // Dynamic content based on the selectedKey
  const contentData = useMemo(() => {
    switch (selectedKey) {
      case 'chat':
        return {
          title: 'Public chat',
          preComponent: isPrivateChatEnabled ? (
            <Suspense fallback={<OLoader />}>
              <ChatDropdown />
            </Suspense>
          ) : (
            <Typography fontSize={16} marginLeft={1}>
              Public Chat
            </Typography>
          ),
          icon: (
            <Badge color="error" variant="dot" invisible={!hasChatNotifications}>
              <AiFillWechat size={24} />
            </Badge>
          ),
          isTitleHidden: true,
          isArrowHidden: true,
          content: (
            <Suspense fallback={<OLoader />}>
              <ChatBox />
            </Suspense>
          ),
        };
      case 'polls':
        return {
          title: 'Polls',
          icon: <MdBarChart size={24} />,
          isArrowHidden: true,
          content: (
            <Suspense fallback={<OLoader />}>
              <PollsBox />
            </Suspense>
          ),
        };
      case 'question-and-answer':
        return {
          title: 'Q&A',
          icon: <QuestionAndAnswerIcon />,
          isArrowHidden: true,
          ...(isHostOrCohost && {
            actionButton: {
              icon: isLoading ? (
                <OSpinner />
              ) : (
                <OPopper icon={<VscThreeBars height={22} width={22} />}>
                  <OButton variant="none" size="small" onClick={handleExportCSV} text="Export CSV file" />
                </OPopper>
              ),
            },
          }),
          content: (
            <Suspense fallback={<OLoader />}>
              <QuestionAndAnswer />
            </Suspense>
          ),
        };
      case 'participants-list':
        return {
          title: 'Attendees',
          icon: <MdGroups size={24} />,
          subtitle: currentPeer?.peer_info.user_type !== EUserType.ATTENDEE && meetingType === 'webinar' && (
            <div className={styles['participants-count']}>
              <div className={styles['participants-count-separator']} />
              <div className={styles['count-section']}>
                <span className={`${styles['count-number']} ${styles['panellist-count']}`}>
                  {participantsPeers?.length}
                </span>
                <span className={styles['count-label']}>
                  {participantsPeers?.length === 1 ? 'Panellist' : 'Panellists'}
                </span>
              </div>
              <span className={styles['count-divider']}>|</span>
              <div className={styles['count-section']}>
                <span className={`${styles['count-number']} ${styles['viewers-count']}`}>
                  {toKFormat(Number(viewersCount))}
                </span>
                <span className={styles['count-label']}>{Number(viewersCount) === 1 ? 'Viewer' : 'Viewers'}</span>
              </div>
            </div>
          ),
          isArrowHidden: true,
          content: (
            <Suspense fallback={<OLoader />}>
              <ParticipantsList />
            </Suspense>
          ),
        };
      case 'virtual-background':
        if (isMobileDevice)
          return {
            title: 'Virtual Backgrounds',
            icon: <TbBackground color="#fff" size={24} />,
            isArrowHidden: true,
            content: (
              <Suspense fallback={<OLoader />}>
                <VirtualBackground />
              </Suspense>
            ),
          };
        else
          return {
            title: '',
            icon: '',
            content: null,
          };

      case 'notes':
        return {
          title: 'Notepad',
          icon: <MdEditNote size={24} />,
          isArrowHidden: true,
          content: (
            <Suspense fallback={<OLoader />}>
              <Notes />
            </Suspense>
          ),
        };
      case 'settings':
        if (isMobileDevice)
          return {
            title: 'Settings',
            icon: <GoGear className="" height={16} width={16} />,
            isArrowHidden: true,
            content: (
              <Suspense fallback={<OLoader />}>
                <SettingsContent />
              </Suspense>
            ),
          };
        else
          return {
            title: '',
            icon: '',
            content: null,
          };
      case 'calculator':
        if (isMobileDevice)
          return {
            title: 'Calculator',
            icon: <CalculatorLogo />,
            isArrowHidden: true,
            customHeader: (
              <div className={styles.calculatorHeaderWrapper}>
                <CalculatorHeader
                  mode={calculatorMode}
                  isReadOnly={false}
                  onModeChange={handleCalculatorModeChange}
                  className="calculatorHeaderInDrawer"
                />
              </div>
            ),
            content: (
              <Suspense fallback={<OLoader />}>
                <Calculator hideHeader={true} />
              </Suspense>
            ),
          };
        else
          return {
            title: '',
            icon: '',
            content: null,
          };

      default:
        return {
          title: '',
          icon: '',
          content: null,
        };
    }
  }, [
    selectedKey,
    isPrivateChatEnabled,
    hasChatNotifications,
    isHostOrCohost,
    isLoading,
    handleExportCSV,
    currentPeer?.peer_info.user_type,
    meetingType,
    participantsPeers?.length,
    viewersCount,
    isMobileDevice,
    calculatorMode,
    handleCalculatorModeChange,
  ]);

  // const isVirtualBackgroundSheetOpen = isMobileDevice && isDrawerOpen && selectedKey === 'virtual-background';

  const isVirtualBackgroundSheetOpen = isDrawerOpen && selectedKey === 'virtual-background';
  const previousVirtualBackgroundState = useRef<boolean>(false);

  useEffect(() => {
    const wasOpen = previousVirtualBackgroundState.current;
    if (isVirtualBackgroundSheetOpen && !wasOpen) {
      sendMessageToAndroid({
        type: EWebViewEvents.UPDATE_TOOLBAR,
        payload: {
          id: generateUID(),
          title: VIRTUAL_BACKGROUND_TITLE,
        },
      });
      sendMessageToAndroid({
        type: EWebViewEvents.WEB_SHEET_OPENED,
        payload: {
          id: generateUID(),
        },
      });
    } else if (!isVirtualBackgroundSheetOpen && wasOpen) {
      sendMessageToAndroid({
        type: EWebViewEvents.WEB_SHEET_DISMISSED,
        payload: {
          id: generateUID(),
        },
      });
      const defaultTitle = meetingType === 'webinar' ? 'Webinar' : 'Meeting';
      const toolbarTitle = roomTitle?.trim() ? roomTitle : defaultTitle;
      sendMessageToAndroid({
        type: EWebViewEvents.UPDATE_TOOLBAR,
        payload: {
          id: generateUID(),
          title: toolbarTitle,
        },
      });
    }
    previousVirtualBackgroundState.current = isVirtualBackgroundSheetOpen;
  }, [isVirtualBackgroundSheetOpen, roomTitle, meetingType]);

  useEffect(() => {
    if (selectedKey === 'settings' && !isMobileDevice) {
      toggleDrawer(false);
    } else if (!isDrawerOpen && isMobileDevice && isRoomSettingsOpen) {
      toggleDrawer(false);
      setIsRoomSettingsOpen(false);
    }
  }, [isMobileDevice, selectedKey, isRoomSettingsOpen, isDrawerOpen, toggleDrawer, setIsRoomSettingsOpen]);

  useEffect(() => {
    if (!isPrivateChatEnabled) {
      setSelectedReceiverData({ key: EMessageType.PUBLIC, value: '' });
    }
  }, [isPrivateChatEnabled, setSelectedReceiverData]);

  useEffect(() => {
    switch (selectedKey) {
      case 'chat':
        if (!isPublicChatEnabled) toggleDrawer(false);
        break;
      case 'polls':
        if (!isPollEnabled) toggleDrawer(false);
        break;
      default:
        break;
    }
  }, [selectedKey, isPublicChatEnabled, isPollEnabled, toggleDrawer, isDrawerOpen]);

  // Sync calculator visibility with drawer state
  useEffect(() => {
    if (selectedKey === 'calculator' && !isDrawerOpen) {
      setIsPersonalCalculatorVisible(false);

      // Stop sharing calculator if it's being shared when drawer closes (only on mobile)
      if (isMobileDevice && sharedCalculatorData.isSharedByMe) {
        handleStopShareCalculator();
      }
    }
  }, [
    selectedKey,
    isDrawerOpen,
    setIsPersonalCalculatorVisible,
    isMobileDevice,
    sharedCalculatorData.isSharedByMe,
    handleStopShareCalculator,
  ]);

  return (
    <div className="chat-container">
      <ODrawer
        {...contentData}
        selectedKey={selectedKey}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={(isOpen) => {
          if (hasActiveLimitedPolls && !isMobileDevice) {
            return;
          }
          toggleDrawer(isOpen);
        }}
      />
    </div>
  );
};

export default DrawerController;
